'use client';

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { AlertCircle, CheckCircle2, ExternalLink, MapPin } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge/badge';
import { Label } from '@/components/ui/label/label';
import { Switch } from '@/components/ui/switch/switch';
import { Checkbox } from '@/components/ui/checkbox/checkbox';
import { Combobox } from '@/components/ui/combobox/combobox';
import { CountrySelect } from '@/components/ui/country-select/country-select';
import { useRegional } from '@/components/providers/regional-provider';

import {
  useCountries,
  useStatesWithRides,
  useRidesByLocation,
} from '../../hooks/use-public-lookups';
import {
  getCapacityBadgeValue,
  isRideFull,
  parseRideMinimumDonation,
  type RideData,
} from '../lib/rideSelection';
import { WaiverDialog } from './WaiverDialog';
import type { PrefetchedRegistrationData, TenantRegistrationConfig } from '../types';

/** Derived ride facts the flow needs for gating and later steps. */
export interface RideSelectionMeta {
  hasStates: boolean;
  isFull: boolean;
  isCancelled: boolean;
  minimumDonation: number;
}

interface RideSelectionStepProps {
  config: TenantRegistrationConfig;
  prefetchedData?: PrefetchedRegistrationData;
  /** User already has an active registration — banner with dashboard link */
  isBlocked: boolean;
  /** Active campaign has registration closed — rides stay browsable, proceed is blocked */
  isCampaignClosed: boolean;
  /** Admin-configured closed copy (campaignSettings.closedMessage), falls back to i18n */
  campaignClosedMessage: string | null;
  countryCode: string;
  stateCode: string;
  rideId: string;
  rideName: string;
  waiverAccepted: boolean;
  joinWaitlist: boolean;
  onCountryChange: (code: string) => void;
  onStateChange: (code: string) => void;
  onRideChange: (id: string, name: string, slug: string) => void;
  onWaiverChange: (accepted: boolean) => void;
  onWaitlistChange: (join: boolean) => void;
  /** Reports derived ride facts whenever the selection or data changes */
  onMetaChange: (meta: RideSelectionMeta) => void;
}

/**
 * Step 1 — country → state → ride cascade with capacity/waitlist, cancelled
 * and postponed handling plus the waiver card. Parameterized lift of the DGR
 * register page step 1.
 */
export function RideSelectionStep({
  config,
  prefetchedData,
  isBlocked,
  isCampaignClosed,
  campaignClosedMessage,
  countryCode,
  stateCode,
  rideId,
  rideName,
  waiverAccepted,
  joinWaitlist,
  onCountryChange,
  onStateChange,
  onRideChange,
  onWaiverChange,
  onWaitlistChange,
  onMetaChange,
}: RideSelectionStepProps) {
  const t = useTranslations('register');
  const tCommon = useTranslations('common');
  const tRoot = useTranslations();
  const tWaiver = useTranslations(config.waiverNamespace);
  const searchParams = useSearchParams();
  const { currency: selectedCurrency } = useRegional();
  const [waiverDialogOpen, setWaiverDialogOpen] = useState(false);

  // ── Cascading lookups ──
  const { data: countriesData = prefetchedData?.countries ?? [], isLoading: isLoadingCountries } =
    useCountries();
  const { data: statesData = prefetchedData?.states ?? [], isLoading: isLoadingStates } =
    useStatesWithRides(countryCode);

  const hasStates = statesData.length > 0;
  const shouldShowStateSelection = Boolean(countryCode) && (isLoadingStates || hasStates);
  const shouldShowRideSelection =
    Boolean(countryCode) && ((hasStates && Boolean(stateCode)) || (!hasStates && !isLoadingStates));

  const { data: ridesData = [] as RideData[], isLoading: isLoadingRides } = useRidesByLocation(
    countryCode,
    stateCode,
    shouldShowStateSelection,
  );

  // ── URL deep-link: /register?eventId=<slug|name|id> ──
  const urlEventId = searchParams.get('eventId') ?? '';
  useEffect(() => {
    if (!urlEventId || rideId || ridesData.length === 0) return;

    const normalized = urlEventId.toLowerCase();
    const matched = ridesData.find((ride: RideData) => {
      const normalizedName = ride.name.toLowerCase().replace(/\s+/g, '-');
      return (
        ride.slug.toLowerCase() === normalized ||
        normalizedName === normalized ||
        ride.id === urlEventId
      );
    });
    if (matched) onRideChange(matched.id, matched.name, matched.slug);
  }, [rideId, ridesData, urlEventId, onRideChange]);

  // ── Selected ride facts ──
  const selectedRideData = useMemo(
    () =>
      ridesData.find((r: RideData) => r.id === rideId) ??
      (rideId
        ? {
            id: rideId,
            name: rideName,
            slug: '',
            capacity: null,
            minimumDonation: null,
            currentRegistrations: 0,
          }
        : undefined),
    [ridesData, rideId, rideName],
  );

  const isSelectedRideFull = isRideFull(selectedRideData);
  const isSelectedRideCancelled = selectedRideData?.isCancelled ?? false;
  const isSelectedRidePostponed = selectedRideData?.isPostponed ?? false;
  const minimumDonation = parseRideMinimumDonation(selectedRideData?.minimumDonation);

  const selectedRidePostponedDateLabel = useMemo(() => {
    const raw = selectedRideData?.postponedDate;
    if (!raw) return null;
    const parsed = new Date(`${raw}T00:00:00`);
    if (Number.isNaN(parsed.getTime())) return null;
    return parsed.toLocaleDateString(undefined, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }, [selectedRideData?.postponedDate]);

  useEffect(() => {
    onMetaChange({
      hasStates,
      isFull: isSelectedRideFull,
      isCancelled: isSelectedRideCancelled,
      minimumDonation,
    });
  }, [hasStates, isSelectedRideFull, isSelectedRideCancelled, minimumDonation, onMetaChange]);

  const shouldShowWaitlistToggle =
    Boolean(rideId) && isSelectedRideFull && !isSelectedRideCancelled;
  const shouldShowWaiverCard = Boolean(rideId);
  const hasMandatoryDonation = minimumDonation > 0;

  return (
    <div>
      <h1 className="mb-2 text-3xl font-bold text-foreground md:text-4xl">{t('findYourRide')}</h1>
      <p className="mb-8 text-muted-foreground">{t('selectCity')}</p>

      {isCampaignClosed && (
        <div role="alert" className="mb-6 rounded-lg border border-amber-300 bg-amber-50 p-4">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <div>
              <p className="font-medium text-amber-900">{t('campaignClosedTitle')}</p>
              <p className="mt-1 text-sm text-amber-800">
                {campaignClosedMessage ?? tRoot(config.campaignClosedBodyKey)}
              </p>
            </div>
          </div>
        </div>
      )}

      {isBlocked && (
        <div className="mb-6 rounded-lg border border-emerald-300 bg-emerald-50 p-4">
          <div className="flex items-start gap-3">
            <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600" />
            <div>
              <p className="font-medium text-emerald-900">{t('alreadyRegisteredMessage')}</p>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('alreadyRegisteredChangeRide')}
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-3">
                <Button
                  type="button"
                  asChild
                  className="bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  <a href="/my/dashboard">{t('goToDashboard')}</a>
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    fetch('/api/auth/sign-out', { method: 'POST', credentials: 'include' }).then(
                      () => {
                        window.location.href = '/register';
                      },
                    );
                  }}
                >
                  {t('registerNewRider')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {/* Country */}
        <div className="space-y-2">
          <Label>{t('country')}</Label>
          <CountrySelect
            countries={countriesData.map((c) => ({ code: c.code, name: c.name }))}
            value={countryCode}
            onValueChange={onCountryChange}
            placeholder={t('selectCountry')}
            disabled={isLoadingCountries}
            className="h-12"
          />
        </div>

        {/* State — only when the country has states/regions */}
        {shouldShowStateSelection && (
          <div className="space-y-2">
            <Label>{t('stateRegion')}</Label>
            <Combobox
              options={statesData.map((s: { code: string; name: string }) => ({
                value: s.code,
                label: s.name,
              }))}
              value={stateCode}
              onValueChange={onStateChange}
              placeholder={t('selectState')}
              searchPlaceholder={t('searchStates')}
              disabled={isLoadingStates}
              className="h-12"
            />
          </div>
        )}

        {/* Ride */}
        {shouldShowRideSelection && (
          <div className="space-y-3 pt-2">
            <div className="flex items-center justify-between">
              <Label>{t('selectYourRide')}</Label>
              {!isLoadingRides && ridesData.length > 0 && (
                <span className="text-sm text-muted-foreground">
                  {t('ridesAvailableCount', { count: ridesData.length })}
                </span>
              )}
            </div>
            <Combobox
              options={ridesData.map((ride: RideData) => ({ value: ride.id, label: ride.name }))}
              value={rideId}
              onValueChange={(id) => {
                const ride = ridesData.find((candidate: RideData) => candidate.id === id);
                onRideChange(id, ride?.name ?? '', ride?.slug ?? '');
              }}
              placeholder={isLoadingRides ? t('loadingRides') : t('chooseARide')}
              searchPlaceholder={`${tCommon('search')}...`}
              emptyMessage={t('noRidesAvailable')}
              disabled={isLoadingRides}
              className="h-12"
              renderValue={(option) => {
                const ride =
                  ridesData.find((candidate: RideData) => candidate.id === option.value) ??
                  selectedRideData;
                if (!ride) return option.label;
                return (
                  <span className="flex min-w-0 items-center gap-3">
                    <MapPin className="h-4 w-4 shrink-0 text-primary" />
                    <span className="truncate font-medium">{ride.name}</span>
                  </span>
                );
              }}
              renderOption={(option) => {
                const ride = ridesData.find((candidate: RideData) => candidate.id === option.value);
                if (!ride) return option.label;

                const capacityBadge = getCapacityBadgeValue(ride.capacity);
                const minimumDonationBadge = parseRideMinimumDonation(ride.minimumDonation);

                return (
                  <div className="flex w-full flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                    <div className="flex min-w-0 items-start gap-3 sm:items-center">
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground sm:mt-0" />
                      <span className="line-clamp-2 font-medium sm:truncate">{ride.name}</span>
                    </div>
                    <div className="flex shrink-0 flex-wrap items-center gap-2 pl-7 sm:ml-auto sm:pl-0">
                      {capacityBadge && (
                        <Badge variant="secondary">
                          {t('capacityBadge', { capacity: capacityBadge })}
                        </Badge>
                      )}
                      {minimumDonationBadge > 0 && (
                        <Badge variant="secondary">
                          {t(
                            'minimumDonationBadge',
                            { amount: minimumDonationBadge },
                            {
                              number: {
                                currency: { style: 'currency', currency: selectedCurrency },
                              },
                            },
                          )}
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              }}
            />
            {rideId && isSelectedRideCancelled && (
              <div className="mt-3 rounded-lg border border-red-300 bg-red-50 p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
                  <div className="flex-1">
                    <p className="font-medium text-red-900">
                      {t('rideCancelledTitle', { rideName: selectedRideData?.name ?? '' })}
                    </p>
                    <p className="mt-1 text-sm text-red-800">{t('rideCancelledDescription')}</p>
                  </div>
                </div>
              </div>
            )}
            {rideId && isSelectedRidePostponed && !isSelectedRideCancelled && (
              <div className="mt-3 rounded-lg border border-amber-300 bg-amber-50 p-4">
                <div className="flex items-start gap-3">
                  <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
                  <div className="flex-1">
                    <p className="font-medium text-amber-900">
                      {t('ridePostponedTitle', { rideName: selectedRideData?.name ?? '' })}
                      {selectedRidePostponedDateLabel ? ` ${selectedRidePostponedDateLabel}` : ''}
                    </p>
                    <p className="mt-1 text-sm text-amber-800">{t('ridePostponedDescription')}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Waitlist toggle when the ride is at capacity */}
        {shouldShowWaitlistToggle && (
          <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="mt-0.5 h-5 w-5 text-amber-600" />
              <div className="flex-1">
                <p className="font-medium text-amber-900">{t('waitlistCapacityReachedTitle')}</p>
                <p className="mt-1 text-sm text-amber-800">
                  {t('waitlistCapacityReachedDescription')}
                </p>
                <div className="mt-3 flex items-center justify-between gap-3">
                  <Label htmlFor="join-waitlist" className="text-sm text-amber-900">
                    {t('waitlistJoinToggleLabel')}
                  </Label>
                  <Switch
                    id="join-waitlist"
                    checked={joinWaitlist}
                    onCheckedChange={onWaitlistChange}
                  />
                </div>
                {hasMandatoryDonation && (
                  <p className="mt-2 text-xs text-amber-800">{t('waitlistSkipsPaymentNotice')}</p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Waiver — shown when a ride is selected */}
        {shouldShowWaiverCard && (
          <div
            className={`mt-4 rounded-lg border p-4 transition-all ${
              waiverAccepted ? 'border-primary/40 bg-primary/5' : 'border-border bg-card'
            }`}
          >
            <div className="flex items-start gap-3">
              <Checkbox
                id="waiver-step1"
                checked={waiverAccepted}
                onCheckedChange={(checked) => onWaiverChange(checked === true)}
                className="mt-0.5"
              />
              <div>
                <Label
                  htmlFor="waiver-step1"
                  className="flex cursor-pointer items-center gap-2 font-medium text-foreground"
                >
                  {tWaiver('step1.title')}
                  <span className="text-destructive">*</span>
                </Label>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                  {tWaiver('step1.agreement')}{' '}
                  <button
                    type="button"
                    onClick={() => setWaiverDialogOpen(true)}
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    {tWaiver('step1.waiverLink')}
                    <ExternalLink className="h-3 w-3" />
                  </button>
                  . {tWaiver('step1.understand')}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      <WaiverDialog
        open={waiverDialogOpen}
        onOpenChange={setWaiverDialogOpen}
        onAccept={() => onWaiverChange(true)}
        waiverNamespace={config.waiverNamespace}
      />
    </div>
  );
}
